import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Sparkles } from 'lucide-react';
import { Lead } from '../../types';
import { useNavigate } from 'react-router-dom';
import { useOrchestrationSpinner } from '../../hooks/useOrchestrationSpinner';
import { isLeadIaOrchestrationKanbanStatus } from '../../constants/leadIaOrchestrationUi';
import { useIsAutorunUser } from '../../context/UserPreferencesContext';
import { useAuth } from '../../context/AuthContext';
import './LeadCard.css';

interface LeadCardProps {
  lead: Lead;
  isDragging?: boolean;
  disableDrag?: boolean;
  onConvert?: (leadId: string) => void;
  onLose?: (leadId: string) => void;
  onOpenLead?: (leadId: string) => void;
  onStartLeadIa?: (leadId: string) => void | Promise<void>;
  onAssumeHumanLeadIa?: (leadId: string) => void | Promise<void>;
  onStopLeadIa?: (leadId: string) => void | Promise<void>;
  /** Org tem IA ligada — usado para mostrar «Atender com IA» em aguardando contato */
  aiOrchestrationEnabled?: boolean;
  /** True enquanto POST /ia/start ainda não retornou (lead ainda pode estar em aguardando_contato). */
  iaStartPending?: boolean;
}

export const LeadCard: React.FC<LeadCardProps> = ({ 
  lead, 
  isDragging = false,
  disableDrag = false,
  onConvert,
  onLose,
  onOpenLead,
  onStartLeadIa,
  onAssumeHumanLeadIa,
  onStopLeadIa,
  aiOrchestrationEnabled = false,
  iaStartPending = false,
}) => {
  const navigate = useNavigate();
  // Badge "autorun": admin/owner enxergam quando o atendente do lead
  // tem aiAutoAttendEnabled=true. Útil pra saber rapidamente se a IA
  // vai responder esse lead sem intervenção humana.
  const atendenteIsAutorun = useIsAutorunUser(lead.atendenteId);
  // Botão "Atender com IA" só aparece pro próprio atendente do lead.
  // Outros usuários (mesmo admins) precisam transferir o lead antes
  // de poder acionar IA pra ele. Cf. `map-funnel-stage-and-add-ia-toggle`.
  const { usuario } = useAuth();
  const isAtendenteOfLead =
    !!usuario?.usuarioId &&
    !!lead.atendenteId &&
    lead.atendenteId === usuario.usuarioId;
  const normalizedPosVendaStatus =
    lead.status === 'fechamento' && lead.statusFechamento === 'vendido'
      ? (lead.posVendaStatus || 'conversoes_ganhas')
      : undefined;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: lead.leadId,
    disabled: disableDrag,
    data: {
      type: 'lead',
      status: lead.status,
      posVendaStatus: normalizedPosVendaStatus,
      atendenteId: lead.atendenteId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    if (onOpenLead) {
      onOpenLead(lead.leadId);
      return;
    }
    navigate(`/leads/${lead.leadId}`);
  };

  const handleConvert = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onConvert) {
      onConvert(lead.leadId);
    }
  };

  const handleLose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLose) {
      onLose(lead.leadId);
    }
  };

  const handleStartIa = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void onStartLeadIa?.(lead.leadId);
  };

  const handleAssumeHuman = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void onAssumeHumanLeadIa?.(lead.leadId);
  };

  const handleStopIa = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void onStopLeadIa?.(lead.leadId);
  };

  const orchestrationSpinning =
    iaStartPending ||
    (isLeadIaOrchestrationKanbanStatus(lead.status) &&
      lead.atendimentoModo === 'ia' &&
      lead.iaRunState === 'running');

  const orchestrationAwaitingHumanSend =
    isLeadIaOrchestrationKanbanStatus(lead.status) &&
    lead.atendimentoModo === 'ia' &&
    lead.iaRunState === 'awaiting_human_send' &&
    !iaStartPending;

  const spinnerLine = useOrchestrationSpinner(orchestrationSpinning, lead.leadId);

  const showObservacaoFooter =
    Boolean(lead.observacao) &&
    !orchestrationSpinning &&
    !orchestrationAwaitingHumanSend;

  const showOrchestrationFooter =
    orchestrationSpinning || orchestrationAwaitingHumanSend || showObservacaoFooter;

  // Check if lead is in fechamento and not yet finalized
  const showActionButtons = lead.status === 'fechamento' && 
    (!lead.statusFechamento || lead.statusFechamento === 'em_andamento');

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`lead-card ${isDragging ? 'dragging' : ''}`}
      onClick={handleClick}
      aria-busy={orchestrationSpinning ? true : undefined}
    >
      <div className="lead-card-header">
        <h4 title={lead.nome}>{lead.nome}</h4>
        <div className="lead-card-badges">
          {isLeadIaOrchestrationKanbanStatus(lead.status) && lead.atendimentoModo === 'ia' && (
            <span className="ia-mode-badge" title="Atendimento por IA">
              IA
            </span>
          )}
          {atendenteIsAutorun && lead.atendimentoModo === 'ia' && (
            <span
              className="ia-autorun-badge"
              title="Atendente em modo autorun (toggle 'Atender com IA' ligado)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1px 6px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                background: '#ecfdf5',
                color: '#059669',
                border: '1px solid #a7f3d0',
                marginLeft: 4,
              }}
            >
              autorun
            </span>
          )}
          {isLeadIaOrchestrationKanbanStatus(lead.status) &&
            lead.atendimentoModo === 'manual' &&
            lead.iaRunState === 'needs_human' && (
              <span className="ia-needs-human-badge" title="IA pediu handoff">
                Humano
              </span>
            )}
          {lead.aiFunnelHandoffAt && (
            <span className="ai-funnel-handoff-badge" title="Entrou no Kanban após o Funil IA">
              Funil IA
            </span>
          )}
          {lead.iaConversationContext?.reactivatedFromLostLeadAt && (
            <span
              className="ia-reactivated-badge"
              title={`Reativado em ${new Date(
                lead.iaConversationContext.reactivatedFromLostLeadAt
              ).toLocaleDateString('pt-BR')} — lead arquivado anteriormente, reaberto pela rotina diária de reativação.`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1px 6px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                background: '#fef3c7',
                color: '#92400e',
                border: '1px solid #fcd34d',
                marginLeft: 4,
              }}
            >
              Reativado
            </span>
          )}
          {lead.status === 'urgente' && (
            <span className="lead-kanban-urgente-badge" title="Coluna Urgente — prioridade humana">
              Urgente
            </span>
          )}
          {lead.iaConversationContext?.handoffBin === 'toxic' && (
            <span className="ai-toxic-badge" title="Lead classificado como conteúdo tóxico">
              TOXICO
            </span>
          )}
          {lead.iaConversationContext?.lowIntentBadgePending && (
            <span className="ai-low-intent-badge" title="Reaberto após arquivo por baixa intenção">
              Intenção baixa
            </span>
          )}
          {lead.isReativacao && (
            <span
              className="ai-reativacao-badge"
              title={lead.reativacaoMotivo || 'Lead marcado como reativação por IA'}
            >
              Funil IA - Reativação
            </span>
          )}
          {lead.unreadCount && lead.unreadCount > 0 && (
            <span className="unread-badge" title={`${lead.unreadCount} mensagem(ns) não lida(s)`}>
              {lead.unreadCount > 9 ? '9+' : lead.unreadCount}
            </span>
          )}
          {lead.anexos && lead.anexos.length > 0 && (
            <span className="attachment-badge">📎</span>
          )}
        </div>
      </div>
      
      <div className="lead-card-body">
        <div className="lead-info-item">
          <span className="label">📞</span>
          <span className="value">{lead.telefone}</span>
        </div>

        <div className="lead-info-item">
          <span className="label">🧑‍💼</span>
          <span className="value">RH: {lead.contatoRh ? 'Sim' : 'Não'}</span>
        </div>

        {lead.contatoRh && (
          <div className="lead-info-item">
            <span className="label">🙋</span>
            <span className="value">Contato RH: {lead.nome}</span>
          </div>
        )}

        {lead.contatoRh && lead.nomeEmpresa && (
          <div className="lead-info-item">
            <span className="label">🏢</span>
            <span className="value">Empresa: {lead.nomeEmpresa}</span>
          </div>
        )}

        {lead.cpf && (
          <div className="lead-info-item">
            <span className="label">🆔</span>
            <span className="value">{lead.cpf}</span>
          </div>
        )}

        {lead.whatsappNumeroEntrada && (
          <div className="lead-info-item">
            <span className="label">📲</span>
            <span className="value">{lead.whatsappNumeroEntrada}</span>
          </div>
        )}
        
        <div className="lead-info-item">
          <span className="label">📧</span>
          <span className="value">{lead.email}</span>
        </div>
        
        {(lead.produtoInteresse || lead.iaConversationContext?.nomeCurso) && (
          <div className="lead-info-item">
            <span className="label">🎯</span>
            <span className="value">
              {lead.produtoInteresse || lead.iaConversationContext?.nomeCurso}
            </span>
          </div>
        )}
        
        {lead.atendenteNome && (
          <div className="lead-info-item">
            <span className="label">👤</span>
            <span className="value">{lead.atendenteNome}</span>
          </div>
        )}

        {typeof lead.interessadoLigarEmMeses === 'number' && lead.interessadoLigarEmMeses > 0 && (
          <div className="lead-info-item">
            <span className="label">📅</span>
            <span className="value value-wrap">Interessado ligar em {lead.interessadoLigarEmMeses} mes(es)</span>
          </div>
        )}
      </div>

      {showOrchestrationFooter && (
        <div className="lead-card-footer">
          {orchestrationSpinning ? (
            <p
              className="observacao orchestration-spinner-line"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <span className="orchestration-spinner-dot" aria-hidden />
              <span className="orchestration-spinner-text">{spinnerLine}</span>
            </p>
          ) : orchestrationAwaitingHumanSend ? (
            <p className="observacao orchestration-awaiting-send" role="status">
              Rascunho pronto — envie a mensagem ou assuma como humano.
            </p>
          ) : (
            <p className="observacao">{lead.observacao}</p>
          )}
        </div>
      )}

      {(aiOrchestrationEnabled || lead.atendimentoModo === 'ia') && (
        <div className="lead-card-ia-actions" onClick={(e) => e.stopPropagation()}>
          {(lead.status === 'aguardando_contato' || lead.status === 'urgente') &&
            aiOrchestrationEnabled &&
            isAtendenteOfLead &&
            onStartLeadIa && (
            <button
              type="button"
              className="btn-lead-ia-start"
              onClick={handleStartIa}
              title="Atender com IA — consome créditos de IA"
            >
              <Sparkles className="btn-lead-ia-icon" size={15} strokeWidth={2} aria-hidden />
              <span>Atender com IA</span>
            </button>
          )}
          {isLeadIaOrchestrationKanbanStatus(lead.status) && lead.atendimentoModo === 'ia' && (
            <>
              {onAssumeHumanLeadIa && (
                <button type="button" className="btn-lead-ia-human" onClick={handleAssumeHuman}>
                  Assumir humano
                </button>
              )}
              {lead.iaRunState === 'running' && onStopLeadIa && (
                <button type="button" className="btn-lead-ia-stop" onClick={handleStopIa}>
                  Parar IA
                </button>
              )}
            </>
          )}
        </div>
      )}

      {showActionButtons && (
        <div className="lead-card-actions">
          <button 
            className="btn-converter"
            onClick={handleConvert}
          >
            Converter
          </button>
          <button 
            className="btn-perdido"
            onClick={handleLose}
          >
            Perdido
          </button>
        </div>
      )}
    </div>
  );
};
